import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Box,
    CircularProgress,
    TableSortLabel,
    Select,
    MenuItem,
    useTheme,
} from '@mui/material';
import { API_BASE_URL } from '../api/contests';
import { SwipeHint } from "../utils/SwipeHint";
import Medal from "../utils/Medal";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';

interface ParticipantEntry {
    contest_id: number;
    contest_name: string;
    result: number | null;
    result_unit: string | null;
    grouping: string | null;
    ranking?: number;
    total_participants: number | null;
    created_at: string | null;
    participant_data: string | null;
    participant_name: string | null;
    profile_picture: string | null;
    participant_id: number;
    evidence_url: string | null;
}

const ParticipantEntriesPage: React.FC = () => {
    const { participantId } = useParams<{ participantId: string }>();
    const [entries, setEntries] = useState<ParticipantEntry[]>([]);
    const [expandedRow, setExpandedRow] = useState<number | null>(null);
    const [sortBy, setSortBy] = useState<keyof ParticipantEntry | null>(null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [selectedContest, setSelectedContest] = useState<string>(''); // State for filtering contests
    const theme = useTheme();

    useEffect(() => {
        if (participantId) {
            axios
                .get(`${API_BASE_URL}/participant-entries/${participantId}`)
                .then((response) => setEntries(response.data))
                .catch((error) => console.error(error));
        }
    }, [participantId]);

    const handleSort = (column: keyof ParticipantEntry) => {
        const isAsc = sortBy === column && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(column);
    };

    const sortedEntries = React.useMemo(() => {
        if (!sortBy) return entries;

        return [...entries].sort((a, b) => {
            const aValue = a[sortBy] ?? '';
            const bValue = b[sortBy] ?? '';

            if (aValue < bValue) {
                return sortOrder === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortOrder === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [entries, sortBy, sortOrder]);

    const truncateText = (text: string | null, limit: number) => {
        if (!text) return '';
        return text.length > limit ? `${text.slice(0, limit)}...` : text;
    };

    // Group entries by contest name and count the number of entries per contest
    const groupedEntries = React.useMemo(() => {
        return sortedEntries.reduce((acc, entry) => {
            const contestName = entry.contest_name || 'Неизвестно';
            if (!acc[contestName]) acc[contestName] = { entries: [], count: 0 };
            acc[contestName].entries.push(entry);
            acc[contestName].count += 1;
            return acc;
        }, {} as Record<string, { entries: ParticipantEntry[]; count: number }>);
    }, [sortedEntries]);

    // Get unique contest names for the dropdown with the number of entries in brackets
    const contestNames = Object.keys(groupedEntries);

    // Filter entries if a specific contest is selected
    const filteredEntries = selectedContest
        ? groupedEntries[selectedContest]?.entries ?? []
        : sortedEntries;

    return !entries.length ? (
        <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
        </Box>
    ) : (
        <div>
            <Box mb={2}>
                <Select
                    value={selectedContest}
                    onChange={(e) => setSelectedContest(e.target.value)}
                    displayEmpty
                    sx={{
                        width: 200,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                    }}
                >
                    <MenuItem value="">Все зарубы ({entries.length})</MenuItem>
                    {contestNames.map((contestName) => (
                        <MenuItem key={contestName} value={contestName}>
                            {contestName} ({groupedEntries[contestName].count})
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            <TableContainer
                component={Paper}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                }}
            >
                <Box display="flex" justifyContent="center" mb={1}>
                    <SwipeHint />
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {[
                                { label: 'Заруба', field: 'contest_name' },
                                { label: 'Результат', field: 'result' },
                                { label: 'Группа', field: 'grouping' },
                                { label: 'Рейтинг', field: 'ranking' },
                                { label: 'Всего участников', field: '' },
                                { label: 'Дата', field: 'created_at' },
                                { label: 'Заметки участника', field: '' },
                            ].map((column, index) => (
                                <TableCell
                                    key={index}
                                    sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: theme.palette.background.default,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {column.field ? (
                                        <TableSortLabel
                                            active={sortBy === column.field}
                                            direction={sortBy === column.field ? sortOrder : 'asc'}
                                            onClick={() => handleSort(column.field as keyof ParticipantEntry)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredEntries.map((entry, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    backgroundColor:
                                        index % 2 === 0
                                            ? theme.palette.background.default
                                            : theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                <TableCell>
                                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                                        <Box display="flex" alignItems="center" mb={0.5}>
                                            {entry.ranking === 1 && (
                                                <EmojiEventsIcon
                                                    fontSize="small"
                                                    sx={{ color: theme.palette.warning.main, mr: 1 }}
                                                />
                                            )}
                                            <Link
                                                to={`/contest/${entry.contest_id}`}
                                                style={{
                                                    textDecoration: 'none',
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {entry.contest_name || 'Неизвестно'}
                                            </Link>
                                        </Box>
                                        {entry.evidence_url && (
                                            <Box mt={0.5} display="flex" alignItems="center">
                                                <Link
                                                    to={entry.evidence_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: theme.palette.primary.main,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <OndemandVideoIcon fontSize="small" sx={{ mr: 1 }} />
                                                    Видео
                                                </Link>
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>{entry.result ?? '—'}</TableCell>
                                <TableCell>
                                    <Link
                                        to={`/contest/${entry.contest_id}/?tab=1&group=${encodeURIComponent(
                                            entry.grouping ?? ''
                                        )}`}
                                        style={{
                                            textDecoration: 'none',
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {entry.grouping ?? '-'}
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    <Medal ranking={entry.ranking} />
                                </TableCell>
                                <TableCell>{entry.total_participants ?? '—'}</TableCell>
                                <TableCell>
                                    {entry.created_at
                                        ? new Date(entry.created_at).toLocaleString('ru-RU')
                                        : '—'}
                                </TableCell>
                                <TableCell style={{ maxWidth: '150px', wordWrap: 'break-word' }}>
                                    {expandedRow === index ? (
                                        <>
                                            {entry.participant_data ?? 'Нет данных'}
                                            <Button
                                                onClick={() => setExpandedRow(null)}
                                                sx={{ textTransform: 'none', fontSize: '12px', ml: 1 }}
                                            >
                                                Скрыть
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            {truncateText(entry.participant_data, 50) ?? 'Нет данных'}
                                            {entry.participant_data && entry.participant_data.length > 50 && (
                                                <Button
                                                    onClick={() => setExpandedRow(index)}
                                                    sx={{ textTransform: 'none', fontSize: '12px', ml: 1 }}
                                                >
                                                    Читать далее
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default ParticipantEntriesPage;
